console.log('🚀 [AdminPanel.jsx] Ładowanie AdminPanel');

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { api } from '../utils/api';
import { useToast } from '../context/ToastContext';
import { useDialog } from './Dialog';
import { AdminContentSkeleton } from './Skeleton';
import { Breadcrumbs } from './Breadcrumbs';
import { AnimatedCounter } from './AnimatedCounter';
import {
  DynamicApplicationFields,
  initialValuesFromSchema,
  validateClient,
  buildResponsesPayload,
  valuesFromApplicationRow,
} from './DynamicApplicationFields';

console.log('✅ [AdminPanel.jsx] Importy załadowane');

const APPLICATION_CONTENT_EDIT_FIELDS = [
  { name: 'ic_name', placeholder: 'Imię i nazwisko | UID', type: 'text' },
  { name: 'sang_rank', placeholder: 'Stopień w SANG', type: 'text' },
  { name: 'sang_duration_units', placeholder: 'Jak długo w SANG i w jakich jednostkach?', type: 'textarea' },
  { name: 'motivation', placeholder: 'Motywacja / dlaczego dołączyć', type: 'textarea' },
  { name: 'why_this_department', placeholder: 'Dlaczego ten wydział?', type: 'textarea' },
  { name: 'similar_department_experience', placeholder: 'Doświadczenie w podobnym wydziale', type: 'textarea' },
  { name: 'reaction_rules_violation', placeholder: 'Reakcja na złamanie zasad (wyższy stopniem)', type: 'textarea' },
  { name: 'reaction_conflict', placeholder: 'Reakcja na konflikt między żołnierzami', type: 'textarea' },
  { name: 'ooc_age', placeholder: 'Wiek OOC', type: 'number', min: 13, max: 120 },
  { name: 'availability', placeholder: 'Aktywność', type: 'text' },
  { name: 'management_experience', placeholder: 'Doświadczenie w zarządzaniu grupą', type: 'textarea' },
  { name: 'training_experience', placeholder: 'Doświadczenie w prowadzeniu szkoleń', type: 'textarea' },
  { name: 'good_training_vision', placeholder: 'Wizja dobrego szkolenia', type: 'textarea' },
  { name: 'good_member_traits', placeholder: 'Cechy dobrego członka', type: 'textarea' },
  { name: 'stress_handling', placeholder: 'Radzenie sobie ze stresem', type: 'textarea' },
  { name: 'why_choose_you', placeholder: 'Dlaczego wybrać Ciebie?', type: 'textarea' },
  { name: 'about_me_player', placeholder: 'O sobie jako graczu', type: 'textarea' },
];

function AdminPanel() {
  console.log('🎨 [AdminPanel.jsx] Renderowanie AdminPanel');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [applications, setApplications] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [blacklist, setBlacklist] = useState([]);
  const [users, setUsers] = useState([]);
  const [usersPagination, setUsersPagination] = useState({ page: 1, per_page: 15, total: 0, total_pages: 1 });
  const [usersPageLoading, setUsersPageLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [showTestForm, setShowTestForm] = useState(false);
  const [testSchema, setTestSchema] = useState(null);
  const [testSchemaLoading, setTestSchemaLoading] = useState(false);
  const [testFormData, setTestFormData] = useState({ test_discord_id: '' });
  const [userPreview, setUserPreview] = useState(null);
  const [showUserPreviewModal, setShowUserPreviewModal] = useState(false);
  const [showAddBlacklistForUser, setShowAddBlacklistForUser] = useState(false);
  const [blacklistForm, setBlacklistForm] = useState({ scope: 'both', reason: '', duration_days: '' });
  const navigate = useNavigate();
  const location = useLocation();
  const { unit } = useParams();
  const base = `/${unit}`;
  const { success, error: showError } = useToast();
  const { confirm, prompt } = useDialog();
  const [hasAccess, setHasAccess] = useState(null);
  const [adminMe, setAdminMe] = useState({
    isApplicationContentEditor: false,
  });
  const [editApplicationModal, setEditApplicationModal] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [savingApplicationContent, setSavingApplicationContent] = useState(false);
  
  // Sync activeTab with URL hash (e.g. /admin#users)
  useEffect(() => {
    const hash = (location.hash || '').replace(/^#/, '');
    if (hash && ['dashboard', 'applications', 'complaints', 'blacklist', 'users'].includes(hash)) {
      setActiveTab(hash);
    }
  }, [location.hash]);

  useEffect(() => {
    checkAdminAccess();
  }, [unit]);
  
  // Przy wejściu na panel (Statystyki) – od razu pobierz wszystko (jedna seria requestów w Network).
  // Przełączanie na Podania/Skargi/Blacklista/Użytkownicy korzysta z już załadowanych danych.
  useEffect(() => {
    if (hasAccess === true) {
      loadAllData();
    }
  }, [hasAccess, unit]);

  // Co minutę odśwież wszystkie dane w tle (bez pokazywania loadera).
  useEffect(() => {
    if (hasAccess !== true) return;
    const interval = setInterval(() => {
      loadAllData(true);
    }, 60 * 1000);
    return () => clearInterval(interval);
  }, [hasAccess, unit]);

  useEffect(() => {
    if (!showTestForm) return undefined;
    let cancelled = false;
    setTestSchemaLoading(true);
    setTestSchema(null);
    (async () => {
      try {
        const d = await api.get(`/get-application-form-schema?unit=${unit}`);
        if (cancelled) return;
        setTestSchema(d.schema);
        setTestFormData((prev) => ({
          test_discord_id: prev.test_discord_id || '',
          ...initialValuesFromSchema(d.schema.fields),
        }));
      } catch (err) {
        if (!cancelled) {
          showError(err.message || 'Nie udało się załadować szablonu formularza');
          setShowTestForm(false);
        }
      } finally {
        if (!cancelled) setTestSchemaLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showTestForm, unit]);

  const checkAdminAccess = async () => {
    setHasAccess(null);
    try {
      const data = await api.get(`/api-me?unit=${unit}`);
      if (!data.isAdmin) {
        console.log('❌ [AdminPanel] User is not admin, redirecting to dashboard');
        showError('Brak uprawnień do panelu administracyjnego');
        navigate(`${base}/dashboard`);
        setHasAccess(false);
      } else {
        setAdminMe({
          isApplicationContentEditor: !!data.isApplicationContentEditor,
        });
        setHasAccess(true);
      }
    } catch (err) {
      console.error('Failed to check admin access:', err);
      showError('Nie udało się sprawdzić uprawnień');
      navigate(`${base}/dashboard`);
      setHasAccess(false);
    }
  };
  
  /** Pobiera wszystkie dane jednym zestawem requestów (statystyki + podania + skargi + blacklista + użytkownicy). */
  const loadAllData = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const promises = [
        api.get(`/admin-applications?unit=${unit}&status=all`),
        api.get(`/admin-complaints?unit=${unit}&status=all`),
        api.get(`/admin-blacklist?unit=${unit}`)
      ];
      if (!silent) {
        promises.push(api.get(`/admin-users?unit=${unit}&page=1&per_page=15`));
      }
      const results = await Promise.all(promises);
      const appsAll = results[0];
      const complaintsAll = results[1];
      const blacklistData = results[2];
      const usersData = silent ? null : results[3];

      const applicationsList = appsAll.applications || [];
      const complaintsList = complaintsAll.complaints || [];
      const blacklistsList = blacklistData.blacklists || [];
      const usersList = usersData ? (usersData.users || []) : users;
      setApplications(applicationsList);
      setComplaints(complaintsList);
      setBlacklist(blacklistsList);
      if (usersData) {
        setUsers(usersList);
        if (usersData.pagination) setUsersPagination(usersData.pagination);
      }

      setStats({
        applications: {
          pending: applicationsList.filter(a => a.status === 'pending').length,
          accepted: applicationsList.filter(a => a.status === 'accepted').length,
          rejected: applicationsList.filter(a => a.status === 'rejected').length,
          total: applicationsList.length
        },
        complaints: {
          pending: complaintsList.filter(c => c.status === 'pending').length,
          in_progress: complaintsList.filter(c => c.status === 'in_progress').length,
          resolved: complaintsList.filter(c => c.status === 'resolved').length,
          dismissed: complaintsList.filter(c => c.status === 'dismissed').length,
          total: complaintsList.length
        },
        blacklist: {
          total: blacklistsList.length,
          active: blacklistsList.filter(b => !b.expires_at || new Date(b.expires_at) > new Date()).length
        }
      });
    } catch (err) {
      console.error('Failed to load admin data:', err);
      if (!silent && (err.message.includes('403') || err.message.includes('Insufficient permissions'))) {
        showError('Brak uprawnień do panelu administracyjnego');
        navigate(`${base}/dashboard`);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };
  
  const toggleExpanded = (id) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedItems(newExpanded);
  };
  
  const filteredApplications = applications.filter(app => {
    const matchesStatus = filter === 'all' || app.status === filter;
    const matchesSearch = searchTerm === '' ||
      app.ic_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.discord_id?.includes(searchTerm) ||
      app.username?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const filteredComplaints = complaints.filter(complaint => {
    const matchesStatus = filter === 'all' || complaint.status === filter;
    const matchesSearch = searchTerm === '' ||
      complaint.reported_discord_id?.includes(searchTerm) ||
      complaint.complainant_discord_id?.includes(searchTerm) ||
      complaint.description?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });
  
  const filteredBlacklist = blacklist.filter(entry =>
    searchTerm === '' ||
    entry.discord_id.includes(searchTerm) ||
    (entry.discord_username && entry.discord_username.toLowerCase().includes(searchTerm.toLowerCase())) ||
    entry.reason.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredUsers = users.filter(u =>
    searchTerm === '' ||
    u.discord_id.includes(searchTerm) ||
    (u.username && u.username.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const loadUsersPage = async (page) => {
    if (page < 1 || page > usersPagination.total_pages) return;
    setUsersPageLoading(true);
    try {
      const data = await api.get(`/admin-users?unit=${unit}&page=${page}&per_page=15`);
      setUsers(data.users || []);
      if (data.pagination) setUsersPagination(data.pagination);
    } catch (err) {
      console.error('Failed to load users page:', err);
      showError('Nie udało się załadować strony użytkowników');
    } finally {
      setUsersPageLoading(false);
    }
  };

  const openUserPreview = async (user) => {
    try {
      const data = await api.get(`/admin-user-detail?unit=${unit}&user_id=${user.id}`);
      setUserPreview(data);
      setShowUserPreviewModal(true);
      setShowAddBlacklistForUser(false);
      setBlacklistForm({ scope: 'both', reason: '', duration_days: '' });
    } catch (err) {
      console.error('Failed to load user detail:', err);
      showError('Nie udało się załadować podglądu użytkownika');
    }
  };

  const handleAddBlacklistForUser = async (e) => {
    e.preventDefault();
    if (!userPreview?.user?.discord_id || !blacklistForm.reason?.trim()) {
      showError('Wprowadź powód blacklisty');
      return;
    }
    const scopeMap = { podania: 'application', skargi: 'complaint', oba: 'both' };
    const scope = scopeMap[blacklistForm.scope] || 'both';
    try {
      await api.post('/admin-blacklist', {
        target_discord_id: userPreview.user.discord_id,
        reason: blacklistForm.reason.trim(),
        scope,
        unit: unit,
        duration_days: blacklistForm.duration_days ? parseInt(blacklistForm.duration_days, 10) : null
      });
      success('Blacklist nadana');
      setShowAddBlacklistForUser(false);
      setBlacklistForm({ scope: 'both', reason: '', duration_days: '' });
      const data = await api.get(`/admin-user-detail?unit=${unit}&user_id=${userPreview.user.id}`);
      setUserPreview(data);
      loadAllData(true);
    } catch (err) {
      console.error('Failed to add blacklist:', err);
      showError(err.message || 'Nie udało się dodać do blacklisty');
    }
  };

  const scopeLabel = (scope) => {
    if (scope === 'application') return 'Podania';
    if (scope === 'complaint') return 'Skargi';
    if (scope === 'both') return 'Oba';
    return scope;
  };
  
  const handleUpdateApplication = async (id, status, reason = null) => {
    try {
      await api.patch('/admin-applications', { 
        application_id: id, 
        status, 
        decision_reason: reason 
      });
      success(status === 'accepted' ? 'Podanie zatwierdzone' : 'Podanie odrzucone');
      loadAllData(true);
    } catch (err) {
      console.error('Failed to update application:', err);
      showError('Nie udało się zaktualizować podania');
    }
  };

  const openEditApplicationModal = (app) => {
    if (app.form_schema_snapshot?.fields?.length) {
      setEditFormData(valuesFromApplicationRow(app));
      setEditApplicationModal(app);
      return;
    }
    const next = {};
    APPLICATION_CONTENT_EDIT_FIELDS.forEach((f) => {
      if (f.name === 'ooc_age') {
        next[f.name] = app.ooc_age != null ? String(app.ooc_age) : '';
      } else {
        next[f.name] = app[f.name] != null ? String(app[f.name]) : '';
      }
    });
    setEditFormData(next);
    setEditApplicationModal(app);
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveApplicationContent = async (e) => {
    e.preventDefault();
    if (!editApplicationModal) return;
    setSavingApplicationContent(true);
    try {
      const snap = editApplicationModal.form_schema_snapshot;
      if (snap?.fields?.length) {
        const fieldErrors = validateClient(snap.fields, editFormData);
        if (Object.keys(fieldErrors).length > 0) {
          showError(Object.values(fieldErrors)[0] || 'Sprawdź pola formularza');
          setSavingApplicationContent(false);
          return;
        }
        const responsesPayload = buildResponsesPayload(snap.fields, editFormData);
        await api.patch('/admin-application-content', {
          application_id: editApplicationModal.id,
          ...responsesPayload,
        });
      } else {
        const payload = {
          application_id: editApplicationModal.id,
          ...editFormData,
          ooc_age: parseInt(editFormData.ooc_age, 10),
        };
        await api.patch('/admin-application-content', payload);
      }
      success('Treść podania została zaktualizowana');
      setEditApplicationModal(null);
      await loadAllData(true);
    } catch (err) {
      showError(err.message || 'Nie udało się zapisać zmian');
    } finally {
      setSavingApplicationContent(false);
    }
  };
  
  const handleUpdateComplaint = async (id, status, assigned_to = null) => {
    try {
      await api.patch('/admin-complaints', { 
        complaint_id: id, 
        status, 
        assigned_to 
      });
      const msg = status === 'resolved' ? 'Skarga rozwiązana' : status === 'dismissed' ? 'Skarga odrzucona' : 'Skarga w trakcie';
      success(msg);
      loadAllData(true);
    } catch (err) {
      console.error('Failed to update complaint:', err);
      showError('Nie udało się zaktualizować skargi');
    }
  };
  
  const handleAddBlacklist = async () => {
    const discordId = await prompt('Wprowadź Discord ID użytkownika:', '', 'Dodaj do blacklisty');
    if (!discordId) return;
    
    const reason = await prompt('Podaj powód dodania do blacklisty:', '', 'Powód');
    if (!reason) return;
    
    const scopeInput = await prompt('Zakres: wpisz "podania", "skargi" lub "oba" (domyślnie oba):', 'oba', 'Zakres');
    const scopeMap = { podania: 'application', skargi: 'complaint', oba: 'both' };
    const scope = scopeMap[(scopeInput || 'oba').toLowerCase().trim()] || 'both';
    
    const duration = await prompt('Czas trwania w dniach (0 lub puste = bez limitu):', '0', 'Czas trwania');
    
    try {
      await api.post('/admin-blacklist', {
        target_discord_id: discordId,
        reason,
        scope,
        unit: unit,
        duration_days: parseInt(duration) || null
      });
      success('Użytkownik dodany do blacklisty');
      loadAllData(true);
    } catch (err) {
      console.error('Failed to add blacklist:', err);
      showError('Nie udało się dodać do blacklisty');
    }
  };
  
  const handleRemoveBlacklist = async (id) => {
    const confirmed = await confirm('Czy na pewno chcesz usunąć tego użytkownika z blacklisty?', 'Usuń z blacklisty');
    if (!confirmed) return;
    
    try {
      await api.delete(`/admin-blacklist?id=${id}`);
      success('Użytkownik usunięty z blacklisty');
      loadAllData(true);
    } catch (err) {
      console.error('Failed to remove blacklist:', err);
      showError('Nie udało się usunąć z blacklisty');
    }
  };

  const handleTestFormChange = (e) => {
    const { name, value } = e.target;
    setTestFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCreateTestApplication = async (e) => {
    e.preventDefault();
    if (!testSchema?.fields?.length) {
      showError('Poczekaj na załadowanie formularza lub spróbuj ponownie');
      return;
    }
    if (!(testFormData.test_discord_id || '').toString().trim()) {
      showError('Podaj Discord ID testowego użytkownika');
      return;
    }
    const fieldErrors = validateClient(testSchema.fields, testFormData);
    if (Object.keys(fieldErrors).length > 0) {
      showError(Object.values(fieldErrors)[0] || 'Wypełnij wszystkie wymagane pola');
      return;
    }
    const responses = buildResponsesPayload(testSchema.fields, testFormData);
    try {
      await api.post('/admin-create-test-application', {
        unit,
        test_discord_id: String(testFormData.test_discord_id).trim(),
        responses,
      });
      success('Testowe podanie zostało utworzone!');
      setShowTestForm(false);
      setTestSchema(null);
      setTestFormData({ test_discord_id: '' });
      loadAllData(true);
    } catch (err) {
      console.error('Failed to create test application:', err);
      showError(err.response?.data?.error || err.message || 'Nie udało się utworzyć testowego podania');
    }
  };
  
  // Don't render anything if access check is in progress or denied
  if (hasAccess === null) {
    return (
      <div className="admin-page">
        <div className="loading">Sprawdzanie uprawnień...</div>
      </div>
    );
  }
  
  if (hasAccess === false) {
    return null; // Will redirect to dashboard
  }
  
  return (
    <div className="admin-page">
      {/* Admin Header */}
      <header className="admin-header-bar">
        <div className="admin-header-inner">
          <div className="logo">
            <img 
              src="https://cdn.discordapp.com/icons/1423017948582252686/bddd09dc3593933ee012741c09ccf6af.webp?size=128" 
              alt="Logo" 
              className="logo__img"
            />
            <span className="logo__text">San Andreas<span className="logo__dot">.</span>National Guard</span>
          </div>
          <div className="admin-badge">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
            </svg>
            Panel Admin
          </div>
        </div>
      </header>

      <main className="admin-main">
        <div className="admin-layout">
          {/* Sidebar */}
          <aside className="admin-sidebar">
            <nav className="admin-nav">
              <button 
                className={`admin-nav__item ${activeTab === 'dashboard' ? 'active' : ''}`}
                onClick={() => { setActiveTab('dashboard'); location.hash = 'dashboard'; }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="20" x2="12" y2="10"></line>
                  <line x1="18" y1="20" x2="18" y2="4"></line>
                  <line x1="6" y1="20" x2="6" y2="16"></line>
                </svg>
                <span>Statystyki</span>
              </button>
              <button 
                className={`admin-nav__item ${activeTab === 'applications' ? 'active' : ''}`}
                onClick={() => { setActiveTab('applications'); location.hash = 'applications'; }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                </svg>
                <span>Podania</span>
              </button>
              <button 
                className={`admin-nav__item ${activeTab === 'complaints' ? 'active' : ''}`}
                onClick={() => { setActiveTab('complaints'); location.hash = 'complaints'; }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                  <line x1="12" y1="9" x2="12" y2="13"></line>
                  <line x1="12" y1="17" x2="12.01" y2="17"></line>
                </svg>
                <span>Skargi</span>
              </button>
              <button 
                className={`admin-nav__item ${activeTab === 'blacklist' ? 'active' : ''}`}
                onClick={() => { setActiveTab('blacklist'); location.hash = 'blacklist'; }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="15" y1="9" x2="9" y2="15"></line>
                  <line x1="9" y1="9" x2="15" y2="15"></line>
                </svg>
                <span>Blacklista</span>
              </button>
              <button 
                className={`admin-nav__item ${activeTab === 'users' ? 'active' : ''}`}
                onClick={() => { setActiveTab('users'); location.hash = 'users'; }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
                <span>Użytkownicy</span>
              </button>
            </nav>
            <button onClick={() => navigate(`${base}/dashboard`)} className="admin-back-btn">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="19" y1="12" x2="5" y2="12"></line>
                <polyline points="12 19 5 12 12 5"></polyline>
              </svg>
              Powrót do panelu
            </button>
          </aside>

          {/* Content */}
          <div className="admin-content">
            {/* Section Header */}
            <div className="admin-section-header">
              <h1 className="admin-title">
                {activeTab === 'dashboard' && 'Statystyki'}
                {activeTab === 'applications' && 'Podania'}
                {activeTab === 'complaints' && 'Skargi'}
                {activeTab === 'blacklist' && 'Blacklista'}
                {activeTab === 'users' && 'Zarządzanie użytkownikami'}
              </h1>
              <p className="admin-subtitle">
                {activeTab === 'dashboard' && 'Przegląd kluczowych metryk systemu'}
                {activeTab === 'applications' && 'Zarządzaj podaniami rekrutacyjnymi'}
                {activeTab === 'complaints' && 'Przeglądaj i rozwiązuj skargi'}
                {activeTab === 'blacklist' && 'Zarządzaj czarną listą użytkowników'}
                {activeTab === 'users' && 'Przeglądaj użytkowników z zatwierdzonym ID'}
              </p>
            </div>

            {/* Toolbar for Users */}
            {activeTab === 'users' && (
              <div className="admin-toolbar" style={{ marginBottom: 'var(--space-xl)' }}>
                <div className="admin-tabs-inline">
                  <button className="admin-tab active">Zatwierdzone</button>
                </div>
                <div className="admin-search">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8"></circle>
                    <path d="m21 21-4.35-4.35"></path>
                  </svg>
                  <input
                    type="text"
                    placeholder="Szukaj po Discord ID lub nazwie..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
            )}

            {/* Toolbar for Applications/Complaints */}
            {(activeTab === 'applications' || activeTab === 'complaints') && (
              <div className="admin-toolbar">
                <div className="admin-tabs-inline">
                  {activeTab === 'applications' ? (
                    <>
                      <button className={`admin-tab ${filter === 'pending' ? 'active' : ''}`} onClick={() => setFilter('pending')}>Oczekujące</button>
                      <button className={`admin-tab ${filter === 'accepted' ? 'active' : ''}`} onClick={() => setFilter('accepted')}>Zaakceptowane</button>
                      <button className={`admin-tab ${filter === 'rejected' ? 'active' : ''}`} onClick={() => setFilter('rejected')}>Odrzucone</button>
                      <button className={`admin-tab ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>Wszystkie</button>
                    </>
                  ) : (
                    <>
                      <button className={`admin-tab ${filter === 'pending' ? 'active' : ''}`} onClick={() => setFilter('pending')}>Oczekujące</button>
                      <button className={`admin-tab ${filter === 'in_progress' ? 'active' : ''}`} onClick={() => setFilter('in_progress')}>W trakcie</button>
                      <button className={`admin-tab ${filter === 'resolved' ? 'active' : ''}`} onClick={() => setFilter('resolved')}>Rozwiązane</button>
                      <button className={`admin-tab ${filter === 'dismissed' ? 'active' : ''}`} onClick={() => setFilter('dismissed')}>Odrzucone</button>
                      <button className={`admin-tab ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>Wszystkie</button>
                    </>
                  )}
                </div>
                <div className="admin-search">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8"></circle>
                    <path d="m21 21-4.35-4.35"></path>
                  </svg>
                  <input 
                    type="text"
                    placeholder="Szukaj..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                {activeTab === 'applications' && (
                  <button type="button" onClick={() => setShowTestForm(true)} className="btn btn--primary">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="12" y1="5" x2="12" y2="19"></line>
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                    </svg>
                    Testowe podanie
                  </button>
                )}
              </div>
            )}

        {loading ? (
          <AdminContentSkeleton tab={activeTab} />
        ) : (
          <>
            {activeTab === 'dashboard' && stats && (
              <div className="admin-dashboard">
                {/* Stats Grid */}
                <div className="admin-stats-grid">
                  <div className="admin-stat-card">
                    <div className="admin-stat-card__icon" style={{background: 'linear-gradient(135deg, #667eea, #764ba2)'}}>
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14 2 14 8 20 8"></polyline>
                      </svg>
                    </div>
                    <div className="admin-stat-card__content">
                      <div className="admin-stat-card__label">Wszystkie podania</div>
                      <div className="admin-stat-card__value"><AnimatedCounter value={stats.applications.total} delay={50} /></div>
                    </div>
                  </div>

                  <div className="admin-stat-card">
                    <div className="admin-stat-card__icon" style={{background: 'linear-gradient(135deg, #f59e0b, #d97706)'}}>
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"></circle>
                        <path d="M12 6v6l4 2"></path>
                      </svg>
                    </div>
                    <div className="admin-stat-card__content">
                      <div className="admin-stat-card__label">Oczekujące podania</div>
                      <div className="admin-stat-card__value"><AnimatedCounter value={stats.applications.pending} delay={100} /></div>
                    </div>
                  </div>

                  <div className="admin-stat-card">
                    <div className="admin-stat-card__icon" style={{background: 'linear-gradient(135deg, #10b981, #059669)'}}>
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                      </svg>
                    </div>
                    <div className="admin-stat-card__content">
                      <div className="admin-stat-card__label">Zaakceptowane</div>
                      <div className="admin-stat-card__value">{stats.applications.accepted}</div>
                    </div>
                  </div>

                  <div className="admin-stat-card">
                    <div className="admin-stat-card__icon" style={{background: 'linear-gradient(135deg, #3b82f6, #2563eb)'}}>
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                      </svg>
                    </div>
                    <div className="admin-stat-card__content">
                      <div className="admin-stat-card__label">Wszystkie skargi</div>
                      <div className="admin-stat-card__value"><AnimatedCounter value={stats.complaints.total} delay={200} /></div>
                    </div>
                  </div>

                  <div className="admin-stat-card">
                    <div className="admin-stat-card__icon" style={{background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)'}}>
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                        <line x1="12" y1="9" x2="12" y2="13"></line>
                        <line x1="12" y1="17" x2="12.01" y2="17"></line>
                      </svg>
                    </div>
                    <div className="admin-stat-card__content">
                      <div className="admin-stat-card__label">Oczekujące skargi</div>
                      <div className="admin-stat-card__value">{stats.complaints.pending}</div>
                    </div>
                  </div>

                  <div className="admin-stat-card">
                    <div className="admin-stat-card__icon" style={{background: 'linear-gradient(135deg, #ef4444, #dc2626)'}}>
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="15" y1="9" x2="9" y2="15"></line>
                        <line x1="9" y1="9" x2="15" y2="15"></line>
                      </svg>
                    </div>
                    <div className="admin-stat-card__content">
                      <div className="admin-stat-card__label">Blacklista</div>
                      <div className="admin-stat-card__value"><AnimatedCounter value={stats.blacklist.active} delay={300} /></div>
                    </div>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="admin-info" style={{marginBottom: 'var(--space-xl)'}}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="16" x2="12" y2="12"></line>
                    <line x1="12" y1="8" x2="12.01" y2="8"></line>
                  </svg>
                  Kliknij na sekcję w sidebarze, aby przejść do szczegółów
                </div>
              </div>
            )}

            {activeTab === 'applications' && (
              <div className="submissions-list">
                {filteredApplications.length === 0 ? (
                  <div className="admin-empty">
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <circle cx="12" cy="12" r="10"></circle>
                      <path d="M12 6v6l4 2"></path>
                    </svg>
                    <p>Brak podań do wyświetlenia</p>
                  </div>
                ) : (
                  filteredApplications.map(app => (
                    <div key={app.id} className="submission-item">
                      <div className="submission-item__header">
                        <span className={`submission-item__type submission-item__type--${app.status}`}>
                          {app.status === 'pending' ? 'Oczekujące' : 
                           app.status === 'accepted' ? 'Zaakceptowane' : 'Odrzucone'}
                        </span>
                        <span className="submission-item__date">
                          {new Date(app.created_at).toLocaleString('pl-PL')}
                        </span>
                      </div>
                      <h3 className="submission-item__author">
                        {(app.responses && app.responses.ic_name) || app.ic_name} (
                        {(app.responses && app.responses.sang_rank) || app.sang_rank})
                      </h3>
                      <div className="submission-item__content">
                        <p><strong>Discord:</strong> {app.username} ({app.discord_id})</p>
                        <p>
                          <strong>Wiek OOC:</strong>{' '}
                          {(app.responses && app.responses.ooc_age != null
                            ? app.responses.ooc_age
                            : app.ooc_age) ?? '—'}{' '}
                          lat
                        </p>
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-sm)', marginTop: 'var(--space-md)' }}>
                        <button 
                          className="admin-table-btn admin-table-btn--primary"
                          onClick={() => toggleExpanded(app.id)}
                        >
                          {expandedItems.has(app.id) ? 'Zwiń szczegóły' : 'Rozwiń szczegóły'}
                        </button>
                        {adminMe.isApplicationContentEditor && app.status === 'pending' && (
                          <button
                            type="button"
                            className="admin-table-btn"
                            style={{ borderColor: 'var(--accent-primary)', color: 'var(--accent-primary)' }}
                            onClick={() => openEditApplicationModal(app)}
                          >
                            Edytuj treść podania
                          </button>
                        )}
                      </div>
                      {expandedItems.has(app.id) && (
                        <div className="submission-details" style={{marginTop: 'var(--space-lg)', padding: 'var(--space-lg)', background: 'rgba(255,255,255,0.03)', borderRadius: 'var(--radius-md)'}}>
                          {app.form_schema_snapshot?.fields?.length ? (
                            app.form_schema_snapshot.fields.map((f) => {
                              const r =
                                app.responses && typeof app.responses === 'object' ? app.responses : {};
                              let val = r[f.id];
                              if (val === undefined || val === null) val = app[f.id];
                              if (val === undefined || val === null || val === '') return null;
                              return (
                                <div key={f.id} style={{ marginBottom: 'var(--space-md)' }}>
                                  <strong style={{ color: 'var(--text-tertiary)' }}>{f.label}:</strong>
                                  <p
                                    style={{
                                      marginTop: 'var(--space-xs)',
                                      color: 'var(--text-secondary)',
                                      whiteSpace: 'pre-wrap',
                                    }}
                                  >
                                    {String(val)}
                                  </p>
                                </div>
                              );
                            })
                          ) : (
                            <>
                              {[
                                { key: 'sang_duration_units', label: 'Jak długo w SANG i w jakich jednostkach?' },
                                { key: 'motivation', label: 'Dlaczego dołączyć do MP?' },
                                { key: 'why_this_department', label: 'Dlaczego ten wydział?' },
                                { key: 'similar_department_experience', label: 'Doświadczenie w podobnym wydziale' },
                                { key: 'reaction_rules_violation', label: 'Reakcja na złamanie zasad (wyższy stopniem)' },
                                { key: 'reaction_conflict', label: 'Reakcja na konflikt między żołnierzami' },
                                { key: 'availability', label: 'Aktywność' },
                                { key: 'management_experience', label: 'Doświadczenie w zarządzaniu grupą' },
                                { key: 'training_experience', label: 'Doświadczenie w prowadzeniu szkoleń' },
                                { key: 'good_training_vision', label: 'Wizja dobrego szkolenia' },
                                { key: 'good_member_traits', label: 'Cechy dobrego członka MP' },
                                { key: 'stress_handling', label: 'Radzenie sobie ze stresem' },
                                { key: 'why_choose_you', label: 'Dlaczego wybrać Ciebie?' },
                                { key: 'about_me_player', label: 'O sobie jako graczu' },
                              ].map(
                                ({ key, label }) =>
                                  app[key] != null &&
                                  app[key] !== '' && (
                                    <div key={key} style={{ marginBottom: 'var(--space-md)' }}>
                                      <strong style={{ color: 'var(--text-tertiary)' }}>{label}:</strong>
                                      <p
                                        style={{
                                          marginTop: 'var(--space-xs)',
                                          color: 'var(--text-secondary)',
                                          whiteSpace: 'pre-wrap',
                                        }}
                                      >
                                        {app[key]}
                                      </p>
                                    </div>
                                  )
                              )}
                              {!app.sang_duration_units && app.experience && (
                                <>
                                  <div style={{ marginBottom: 'var(--space-md)' }}>
                                    <strong style={{ color: 'var(--text-tertiary)' }}>Doświadczenia:</strong>
                                    <p style={{ marginTop: 'var(--space-xs)', color: 'var(--text-secondary)' }}>
                                      {app.experience}
                                    </p>
                                  </div>
                                  <div style={{ marginBottom: 'var(--space-md)' }}>
                                    <strong style={{ color: 'var(--text-tertiary)' }}>Motywacja:</strong>
                                    <p style={{ marginTop: 'var(--space-xs)', color: 'var(--text-secondary)' }}>
                                      {app.motivation}
                                    </p>
                                  </div>
                                  <div style={{ marginBottom: 'var(--space-md)' }}>
                                    <strong style={{ color: 'var(--text-tertiary)' }}>Dostępność:</strong>
                                    <p style={{ marginTop: 'var(--space-xs)', color: 'var(--text-secondary)' }}>
                                      {app.availability}
                                    </p>
                                  </div>
                                  <div style={{ marginBottom: 'var(--space-md)' }}>
                                    <strong style={{ color: 'var(--text-tertiary)' }}>Dobre cechy:</strong>
                                    <p style={{ marginTop: 'var(--space-xs)', color: 'var(--text-secondary)' }}>
                                      {app.good_traits}
                                    </p>
                                  </div>
                                  <div>
                                    <strong style={{ color: 'var(--text-tertiary)' }}>Złe cechy:</strong>
                                    <p style={{ marginTop: 'var(--space-xs)', color: 'var(--text-secondary)' }}>
                                      {app.bad_traits}
                                    </p>
                                  </div>
                                </>
                              )}
                            </>
                          )}
                        </div>
                      )}
                      {app.status === 'pending' && (
                        <div className="admin-table-actions" style={{marginTop: 'var(--space-lg)'}}>
                          <button onClick={() => handleUpdateApplication(app.id, 'accepted')} className="admin-table-btn admin-table-btn--primary">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"></polyline></svg>
                            Zaakceptuj
                          </button>
                          <button onClick={async () => {
                            const reason = await prompt('Podaj powód odrzucenia podania:', '', 'Odrzuć podanie');
                            if (reason) handleUpdateApplication(app.id, 'rejected', reason);
                          }} className="admin-table-btn admin-table-btn--danger">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                            Odrzuć
                          </button>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}

            {activeTab === 'complaints' && (
              <div className="submissions-list">
                {filteredComplaints.length === 0 ? (
                  <div className="admin-empty">
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                    </svg>
                    <p>Brak skarg do wyświetlenia</p>
                  </div>
                ) : (
                  filteredComplaints.map(complaint => (
                    <div key={complaint.id} className="submission-item">
                      <div className="submission-item__header">
                        <span className={`submission-item__type submission-item__type--${complaint.status}`}>
                          {complaint.status === 'pending' ? 'Oczekujące' : 
                           complaint.status === 'in_progress' ? 'W trakcie' : 
                           complaint.status === 'resolved' ? 'Rozwiązane' : 'Odrzucone'}
                        </span>
                        <span className="submission-item__date">
                          {new Date(complaint.created_at).toLocaleString('pl-PL')}
                        </span>
                      </div>
                      <h3 className="submission-item__author">Skarga #{complaint.id}</h3>
                      <div className="submission-item__content">
                        <p><strong>Zgłaszający:</strong> {complaint.complainant_username} ({complaint.complainant_discord_id})</p>
                        <p><strong>Zgłaszany:</strong> {complaint.reported_display} ({complaint.reported_discord_id})</p>
                      </div>
                      <button 
                        className="admin-table-btn admin-table-btn--primary"
                        onClick={() => toggleExpanded(`c-${complaint.id}`)}
                        style={{marginTop: 'var(--space-md)'}}
                      >
                        {expandedItems.has(`c-${complaint.id}`) ? 'Zwiń szczegóły' : 'Rozwiń szczegóły'}
                      </button>
                      {expandedItems.has(`c-${complaint.id}`) && (
                        <div className="submission-details" style={{marginTop: 'var(--space-lg)', padding: 'var(--space-lg)', background: 'rgba(255,255,255,0.03)', borderRadius: 'var(--radius-md)'}}>
                          <div style={{marginBottom: 'var(--space-md)'}}>
                            <strong style={{color: 'var(--text-tertiary)'}}>Opis:</strong>
                            <p style={{marginTop: 'var(--space-xs)', color: 'var(--text-secondary)'}}>{complaint.description}</p>
                          </div>
                          {complaint.evidence_links && complaint.evidence_links.length > 0 && (
                            <div>
                              <strong style={{color: 'var(--text-tertiary)'}}>Dowody:</strong>
                              <ul style={{marginTop: 'var(--space-xs)', paddingLeft: 'var(--space-lg)'}}>
                                {complaint.evidence_links.map((link, i) => (
                                  <li key={i} style={{marginBottom: 'var(--space-xs)'}}>
                                    <a href={link} target="_blank" rel="noreferrer" style={{color: 'var(--accent-primary)'}}>{link}</a>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                      {complaint.status === 'pending' && (
                        <div className="admin-table-actions" style={{marginTop: 'var(--space-lg)'}}>
                          <button onClick={() => handleUpdateComplaint(complaint.id, 'in_progress')} className="admin-table-btn">
                            Rozpocznij
                          </button>
                          <button onClick={() => handleUpdateComplaint(complaint.id, 'resolved')} className="admin-table-btn admin-table-btn--primary">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"></polyline></svg>
                            Rozwiąż
                          </button>
                          <button onClick={() => handleUpdateComplaint(complaint.id, 'dismissed')} className="admin-table-btn admin-table-btn--danger">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                            Odrzuć
                          </button>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
            
            {activeTab === 'blacklist' && (
              <div className="blacklist-section">
                <div className="admin-toolbar" style={{marginBottom: 'var(--space-xl)'}}>
                  <button onClick={handleAddBlacklist} className="btn btn--primary">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="12" y1="5" x2="12" y2="19"></line>
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                    </svg>
                    Dodaj do blacklisty
                  </button>
                  <div className="admin-search">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="11" cy="11" r="8"></circle>
                      <path d="m21 21-4.35-4.35"></path>
                    </svg>
                    <input 
                      type="text"
                      placeholder="Szukaj..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
                <div className="admin-table-wrapper">
                  {filteredBlacklist.length === 0 ? (
                    <div className="admin-empty">
                      <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="15" y1="9" x2="9" y2="15"></line>
                        <line x1="9" y1="9" x2="15" y2="15"></line>
                      </svg>
                      <p>Blacklista jest pusta</p>
                    </div>
                  ) : (
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Discord ID</th>
                          <th>Powód</th>
                          <th>Zakres</th>
                          <th>Wygasa</th>
                          <th>Akcje</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredBlacklist.map(entry => (
                          <tr key={entry.id}>
                            <td>
                              {entry.discord_username ? (
                                <span title={entry.discord_id}>{entry.discord_username}</span>
                              ) : (
                                <code>{entry.discord_id}</code>
                              )}
                              {entry.discord_username && (
                                <span className="admin-table-id-hint" style={{ display: 'block', fontSize: '0.7rem', color: 'var(--text-tertiary)' }}>{entry.discord_id}</span>
                              )}
                            </td>
                            <td>{entry.reason}</td>
                            <td><span className="submission-item__type" style={{fontSize: '0.75rem'}}>{scopeLabel(entry.scope)}</span></td>
                            <td>{entry.expires_at ? new Date(entry.expires_at).toLocaleString('pl-PL') : 'Nigdy'}</td>
                            <td>
                              <div className="admin-table-actions">
                                <button onClick={() => handleRemoveBlacklist(entry.id)} className="admin-table-btn admin-table-btn--danger">
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polyline points="3 6 5 6 21 6"></polyline>
                                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                  </svg>
                                  Usuń
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'users' && (
              <div className="admin-table-wrapper">
                {usersPageLoading ? (
                  <div className="admin-empty">
                    <p>Ładowanie użytkowników…</p>
                  </div>
                ) : filteredUsers.length === 0 ? (
                  <div className="admin-empty">
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                      <circle cx="9" cy="7" r="4"></circle>
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                      <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                    </svg>
                    <p>Brak użytkowników do wyświetlenia</p>
                  </div>
                ) : (
                  <>
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Nazwa Discord</th>
                          <th>Discord ID</th>
                          <th>Zatwierdzono</th>
                          <th>Akcje</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredUsers.map(u => (
                          <tr key={u.id}>
                            <td>{u.username || '—'}</td>
                            <td><code>{u.discord_id}</code></td>
                            <td>{u.created_at ? new Date(u.created_at).toLocaleDateString('pl-PL') : '—'}</td>
                            <td>
                              <div className="admin-table-actions">
                                <button
                                  type="button"
                                  className="admin-table-btn admin-table-btn--primary"
                                  onClick={() => openUserPreview(u)}
                                >
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8z"></path>
                                    <circle cx="12" cy="12" r="3"></circle>
                                  </svg>
                                  Podgląd
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {usersPagination.total_pages > 1 && (
                      <div className="admin-pagination">
                        <span className="admin-pagination__info">
                          Strona {usersPagination.page} z {usersPagination.total_pages} ({usersPagination.total} użytkowników)
                        </span>
                        <div className="admin-pagination__buttons">
                          <button
                            type="button"
                            className="admin-table-btn admin-table-btn--secondary"
                            disabled={usersPagination.page <= 1 || usersPageLoading}
                            onClick={() => loadUsersPage(usersPagination.page - 1)}
                          >
                            Poprzednia
                          </button>
                          <button
                            type="button"
                            className="admin-table-btn admin-table-btn--secondary"
                            disabled={usersPagination.page >= usersPagination.total_pages || usersPageLoading}
                            onClick={() => loadUsersPage(usersPagination.page + 1)}
                          >
                            Następna
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </>
        )}
          </div>
        </div>
      </main>
      
      {/* Modal Podgląd użytkownika (inspiracja matsoor.space – bez Gmail) */}
      {showUserPreviewModal && userPreview && (
        <div className="admin-modal" onClick={() => { setShowUserPreviewModal(false); setUserPreview(null); setShowAddBlacklistForUser(false); }}>
          <div className="admin-modal__backdrop"></div>
          <div className="admin-modal__box admin-modal__box--wide" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="admin-modal__close" onClick={() => { setShowUserPreviewModal(false); setUserPreview(null); setShowAddBlacklistForUser(false); }} aria-label="Zamknij">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
            <div className="user-view-header">
              {userPreview.user.avatar_url ? (
                <img src={userPreview.user.avatar_url} alt="" className="user-view-avatar" />
              ) : (
                <div className="user-view-avatar user-view-avatar--placeholder">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                </div>
              )}
              <div className="user-view-info">
                <h2 className="admin-modal__title user-view-name">{userPreview.user.username || 'Użytkownik'}</h2>
                <code className="user-view-discord-id">{userPreview.user.discord_id}</code>
              </div>
            </div>
            <div className="user-view-content">
              <div className="user-view-section">
                <h3>Informacje</h3>
                <div className="user-view-grid">
                  <div className="user-view-item">
                    <span className="user-view-label">Data rejestracji</span>
                    <span className="user-view-value">
                      {userPreview.user.created_at ? new Date(userPreview.user.created_at).toLocaleDateString('pl-PL') : '—'}
                    </span>
                  </div>
                  <div className="user-view-item">
                    <span className="user-view-label">Status blacklisty</span>
                    <span className="user-view-value">
                      {userPreview.blacklist && userPreview.blacklist.length > 0
                        ? scopeLabel(userPreview.blacklist[0].scope) + ' – ' + (userPreview.blacklist[0].reason || '')
                        : 'Brak'}
                    </span>
                  </div>
                </div>
              </div>
              <div className="user-view-section">
                <h3>Statystyki podań</h3>
                <div className="user-view-grid user-view-grid--4">
                  <div className="user-view-stat">
                    <span className="user-view-stat-value">{userPreview.stats?.applications?.total ?? 0}</span>
                    <span className="user-view-stat-label">Łącznie</span>
                  </div>
                  <div className="user-view-stat">
                    <span className="user-view-stat-value">{userPreview.stats?.applications?.pending ?? 0}</span>
                    <span className="user-view-stat-label">Oczekujące</span>
                  </div>
                  <div className="user-view-stat user-view-stat--success">
                    <span className="user-view-stat-value">{userPreview.stats?.applications?.accepted ?? 0}</span>
                    <span className="user-view-stat-label">Zaakceptowane</span>
                  </div>
                  <div className="user-view-stat user-view-stat--danger">
                    <span className="user-view-stat-value">{userPreview.stats?.applications?.rejected ?? 0}</span>
                    <span className="user-view-stat-label">Odrzucone</span>
                  </div>
                </div>
              </div>
              <div className="user-view-section">
                <h3>Statystyki skarg</h3>
                <div className="user-view-grid user-view-grid--4">
                  <div className="user-view-stat">
                    <span className="user-view-stat-value">{userPreview.stats?.complaints?.total ?? 0}</span>
                    <span className="user-view-stat-label">Łącznie</span>
                  </div>
                  <div className="user-view-stat">
                    <span className="user-view-stat-value">{userPreview.stats?.complaints?.pending ?? 0}</span>
                    <span className="user-view-stat-label">Nowe</span>
                  </div>
                  <div className="user-view-stat user-view-stat--success">
                    <span className="user-view-stat-value">{userPreview.stats?.complaints?.resolved ?? 0}</span>
                    <span className="user-view-stat-label">Zaakceptowane</span>
                  </div>
                  <div className="user-view-stat user-view-stat--danger">
                    <span className="user-view-stat-value">{userPreview.stats?.complaints?.dismissed ?? 0}</span>
                    <span className="user-view-stat-label">Odrzucone</span>
                  </div>
                </div>
              </div>
              <div className="user-view-section">
                <h3>Zatwierdzone podania</h3>
                <div className="user-view-ids">
                  {userPreview.accepted_applications && userPreview.accepted_applications.length > 0 ? (
                    <ul className="user-view-ids-list">
                      {userPreview.accepted_applications.map(app => (
                        <li key={app.id}>
                          {app.ic_name} ({app.sang_rank}) – {app.updated_at ? new Date(app.updated_at).toLocaleDateString('pl-PL') : new Date(app.created_at).toLocaleDateString('pl-PL')}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    '—'
                  )}
                </div>
              </div>
              <div className="user-view-section user-view-actions-section">
                <h3>Akcje</h3>
                <div className="user-view-actions-buttons">
                  {userPreview.blacklist && userPreview.blacklist.length > 0 ? (
                    <>
                      <span className="user-view-already-blacklisted">Użytkownik ma już blacklistę</span>
                      <button
                        type="button"
                        className="btn btn--secondary"
                        onClick={async () => {
                          const id = userPreview.blacklist[0].id;
                          try {
                            await api.delete(`/admin-blacklist?id=${id}`);
                            success('Blacklist usunięta');
                            const data = await api.get(`/admin-user-detail?unit=${unit}&user_id=${userPreview.user.id}`);
                            setUserPreview(data);
                            loadAllData(true);
                          } catch (err) {
                            showError(err.message || 'Nie udało się usunąć blacklisty');
                          }
                        }}
                      >
                        Usuń z blacklisty
                      </button>
                      <button type="button" className="btn btn--secondary" onClick={() => { setShowUserPreviewModal(false); setUserPreview(null); }}>Zamknij</button>
                    </>
                  ) : !showAddBlacklistForUser ? (
                    <>
                      <button type="button" className="user-view-action-btn user-view-action-btn--danger" onClick={() => setShowAddBlacklistForUser(true)}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
                        Nadaj Blacklist
                      </button>
                      <button type="button" className="btn btn--secondary" onClick={() => { setShowUserPreviewModal(false); setUserPreview(null); }}>Zamknij</button>
                    </>
                  ) : (
                  <form onSubmit={handleAddBlacklistForUser} className="user-view-blacklist-form">
                    <div className="form-group">
                      <label className="form-label">Zakres</label>
                      <select
                        value={blacklistForm.scope}
                        onChange={(e) => setBlacklistForm(prev => ({ ...prev, scope: e.target.value }))}
                        className="admin-modal__input"
                      >
                        <option value="application">Podania</option>
                        <option value="complaint">Skargi</option>
                        <option value="both">Oba</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Powód</label>
                      <input
                        type="text"
                        value={blacklistForm.reason}
                        onChange={(e) => setBlacklistForm(prev => ({ ...prev, reason: e.target.value }))}
                        placeholder="Powód blacklisty"
                        className="admin-modal__input"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Czas (dni, 0 = bez limitu)</label>
                      <input
                        type="number"
                        min="0"
                        value={blacklistForm.duration_days}
                        onChange={(e) => setBlacklistForm(prev => ({ ...prev, duration_days: e.target.value }))}
                        placeholder="0"
                        className="admin-modal__input"
                      />
                    </div>
                    <div className="admin-modal__actions">
                      <button type="submit" className="btn btn--primary">Zastosuj blacklist</button>
                      <button type="button" className="btn btn--secondary" onClick={() => setShowAddBlacklistForUser(false)}>Anuluj</button>
                    </div>
                  </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal – edycja treści podania (wyznaczony edytor jednostki) */}
      {editApplicationModal && (
        <div className="admin-modal" onClick={() => setEditApplicationModal(null)}>
          <div className="admin-modal__backdrop"></div>
          <div className="admin-modal__box" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '640px', maxHeight: '90vh', overflowY: 'auto' }}>
            <button type="button" className="admin-modal__close" onClick={() => setEditApplicationModal(null)} aria-label="Zamknij">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
            <h2 className="admin-modal__title">Edycja treści podania #{editApplicationModal.id}</h2>
            <p className="admin-modal__desc">Zmiany są zapisywane w bazie. Status musi pozostać „oczekujące”.</p>
            <form onSubmit={handleSaveApplicationContent} className="admin-test-form">
              {editApplicationModal.form_schema_snapshot?.fields?.length ? (
                <DynamicApplicationFields
                  schemaFields={editApplicationModal.form_schema_snapshot.fields}
                  values={editFormData}
                  errors={{}}
                  onChange={handleEditFormChange}
                  inputClassName="admin-modal__input"
                />
              ) : (
                APPLICATION_CONTENT_EDIT_FIELDS.map((f) =>
                  f.type === 'textarea' ? (
                    <textarea
                      key={f.name}
                      name={f.name}
                      value={editFormData[f.name] || ''}
                      onChange={handleEditFormChange}
                      rows={3}
                      placeholder={f.placeholder}
                      className="admin-modal__input"
                      style={{ resize: 'vertical', minHeight: '60px' }}
                      required
                    />
                  ) : (
                    <input
                      key={f.name}
                      type={f.type}
                      name={f.name}
                      value={editFormData[f.name] || ''}
                      onChange={handleEditFormChange}
                      placeholder={f.placeholder}
                      className="admin-modal__input"
                      min={f.min}
                      max={f.max}
                      required
                    />
                  )
                )
              )}
              <div className="admin-modal__actions">
                <button type="submit" className="btn btn--primary" disabled={savingApplicationContent}>
                  {savingApplicationContent ? 'Zapisywanie…' : 'Zapisz zmiany'}
                </button>
                <button type="button" className="btn btn--secondary" onClick={() => setEditApplicationModal(null)}>Anuluj</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal – testowe podanie (wymaga Netlify function admin-create-test-application) */}
      {showTestForm && (
        <div className="admin-modal" onClick={() => setShowTestForm(false)}>
          <div className="admin-modal__backdrop"></div>
          <div className="admin-modal__box" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="admin-modal__close" onClick={() => setShowTestForm(false)} aria-label="Zamknij">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
            <h2 className="admin-modal__title">Utwórz testowe podanie</h2>
            <p className="admin-modal__desc">Wypełnij formularz, aby utworzyć testowe podanie rekrutacyjne (ticket na Discord bez pingów).</p>
            <form onSubmit={handleCreateTestApplication} className="admin-test-form">
              <input
                type="text"
                name="test_discord_id"
                value={testFormData.test_discord_id || ''}
                onChange={handleTestFormChange}
                placeholder="Discord ID (np. 123456789012345678)"
                className="admin-modal__input"
                required
              />
              {testSchemaLoading && <p className="admin-modal__desc">Ładowanie pól formularza…</p>}
              {!testSchemaLoading && testSchema?.fields?.length ? (
                <DynamicApplicationFields
                  schemaFields={testSchema.fields}
                  values={testFormData}
                  errors={{}}
                  onChange={handleTestFormChange}
                  inputClassName="admin-modal__input"
                  disabled={testSchemaLoading}
                />
              ) : null}
              {!testSchemaLoading && testSchema && !testSchema.fields?.length ? (
                <p className="admin-modal__desc">Szablon nie zawiera pól — sprawdź migrację 009 i tabelę application_form_schemas.</p>
              ) : null}
              <div className="admin-info" style={{ marginBottom: 'var(--space-lg)' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="16" x2="12" y2="12"></line>
                  <line x1="12" y1="8" x2="12.01" y2="8"></line>
                </svg>
                Testowe podanie utworzy ticket na Discord, ale bez pingowania adminów.
              </div>
              <div className="admin-modal__actions">
                <button
                  type="submit"
                  className="btn btn--primary"
                  disabled={testSchemaLoading || !testSchema?.fields?.length}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  Utwórz podanie
                </button>
                <button type="button" className="btn btn--secondary" onClick={() => setShowTestForm(false)}>Anuluj</button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}

export default AdminPanel;
